import "react-loading-skeleton/dist/skeleton.css";

import { createBrowserHistory } from "history";
import { ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";
import { SkeletonTheme } from "react-loading-skeleton";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "react-query";
import * as ReactRouter from "react-router";

import { notifyBugsnagMutationError, notifyBugsnagQueryError } from "@/logging";
import { ApiProvider } from "@/modules/api/components/ApiProvider";
import { AuthContextProvider } from "@/modules/auth/components/AuthContextProvider";
import { LevelsCongratulationsProvider } from "@/modules/levels/contexts/LevelCongratulationsProvider";
import { Toaster } from "@/modules/notify/Toaster";
import { GlobalStyles } from "@/modules/theme/components/GlobalStyles";
import { ThemeProvider } from "@/modules/theme/components/ThemeProvider";
import { grey, white } from "@/modules/theme/utils/colors";
import { createTheme } from "@/modules/theme/utils/createTheme";
import { UserDataContextProvider } from "@/modules/user/contexts/UserDataContextProvider";

export interface AppProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: AppProvidersProps) {
  const theme = createTheme();
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: notifyBugsnagQueryError,
    }),
    mutationCache: new MutationCache({
      onError: notifyBugsnagMutationError,
    }),
  });

  const basename =
    document.head.querySelector("base")?.getAttribute("href") || "/";

  const history = createBrowserHistory({ basename });

  return (
    <ReactRouter.Router history={history}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <ApiProvider>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <Toaster />
              <AuthContextProvider>
                <UserDataContextProvider>
                  <LevelsCongratulationsProvider>
                    <SkeletonTheme baseColor={grey[100]} highlightColor={white}>
                      {children}
                    </SkeletonTheme>
                  </LevelsCongratulationsProvider>
                </UserDataContextProvider>
              </AuthContextProvider>
            </HelmetProvider>
          </QueryClientProvider>
        </ApiProvider>
      </ThemeProvider>
    </ReactRouter.Router>
  );
}
